import { useCallback, useContext, useEffect, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import { Box, Stack } from "@mui/system";
import theme from "../../../muiTheme";
import ManagementsUpdateHeader from "../ManagementsUpdateHeader/ManagementsUpdateHeader";
import { Grid } from "@mui/material";
import "./ManagementsUpdateGasFlow.scss";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";
import {
  FormattedOwnerData,
  OwnerDataCups,
} from "../../../core/models/ownerData/ownerData.model";
import ManagementsUpdateNewGasFlow from "./ManagementsUpdateNewGasFlow/ManagementsUpdateNewGasFlow";
import {
  GetSuppliesDetailRequestBody,
  SuppliesDetailObject,
} from "../../../core/models/suppliesDetail/suppliesDetail.model";
import {
  formatCupsDetail,
  initialSuppliesDetail,
} from "../../../utils/components/suppliesDetail/suppliesDetail.util";
import { getSuppliesDetail as getSuppliesDetailFetch } from "../../../core/services/suppliesDetail/suppliesDetail.service";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  postCallCenter,
  postImportDocument,
} from "../../../core/services/managements/managements.service";
import {
  ManagementRequestInfo,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  MANAGEMENTS_REGISTER_TYPE_REQUEST,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW,
} from "../../../core/constants/constants";
import {
  getManagementsRequestTypeData,
  getRegisterTypeId,
  initialManagementRequestInfo,
  initialRequestStateData,
} from "../../../utils/components/managements/managements.util";
import { formatDate } from "../../../utils/dateUtils/formatDate.util";
import ManagementsUpdateGasFlowCupsSelection from "./ManagementsUpdateGasFlowCupsSelection/ManagementsUpdateGasFlowCupsSelection";
import { getRecipientId } from "../../../utils/components/services/services.util";

const ManagementsUpdateGasFlow = () => {
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);
  const [step, setStep] = useState<number>(-1);
  const [ownerDataCupSelected, setOwnerDataCupSelected] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  const [cupsDetail, setCupsDetail] = useState<SuppliesDetailObject>(
    initialSuppliesDetail,
  );
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>(initialRequestStateData);
  const [isCupsLoading, setIsCupsLoading] = useState<boolean>(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [managementRequestInfo, setManagementRequestInfo] =
    useState<ManagementRequestInfo>(initialManagementRequestInfo);
  const [contractCode, setContractCode] = useState<string>("");

  const getSuppliesDetail = useCallback(
    async (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      token: string,
      { idCups, idContract }: OwnerDataCups,
      abortSignal: AbortSignal,
    ) => {
      const getSuppliesRequestBody: GetSuppliesDetailRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        isAdmin: isAdministrator.toString(),
        cupsContractId: idContract,
        cupsId: idCups,
      };

      await getSuppliesDetailFetch(getSuppliesRequestBody, token, abortSignal)
        .then((response) => {
          setCupsDetail(formatCupsDetail(response));
          setContractCode(response.CodigoContrato);
          setIsCupsLoading(false);
        })
        .catch((error) => {
          setIsCupsLoading(false);
        });
    },
    [setIsCupsLoading],
  );

  const handleSubmit = (
    incidenceText: string,
    ownerDataCupSelected: OwnerDataCups,
  ): void => {
    const { envirorment, idContract } = ownerDataCupSelected;
    if (userData) {
      const IdTipoRegistro = getRegisterTypeId(
        envirorment,
        MANAGEMENTS_REGISTER_TYPE_REQUEST,
      );
      const requestBody: PostCallCenterRequestBody = {
        TextoIncidencia: incidenceText,
        CodigosContratos: [+contractCode],
        IdsContratos: [+idContract],
        IdTipoRegistro,
        IdSubTipoReclamacion: MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW,
        idUsuarioDestino: getRecipientId(
          IdTipoRegistro,
          MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW,
        ),
        ClaveMedio: isAgent ? "09" : "12",
      };

      setIsUpdateLoading(true);
      setStep(1);

      postCallCenter(requestBody, userData.token)
        .then(([{ IdCallCenter, IdCarpetaIncidencias }]) => ({
          IdCallCenter,
          IdCarpetaIncidencias,
        }))
        .then(async ({ IdCallCenter, IdCarpetaIncidencias }) => {
          if (IdCallCenter === 0) {
            throw new Error("IdCallCenter is 0");
          }
          file &&
            (await postImportDocument(
              IdCallCenter,
              IdCarpetaIncidencias,
              file,
              userData.token,
            ));
          setRequestState(MANAGEMENTS_STATE_PENDING);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT,
              MANAGEMENTS_STATE_PENDING,
              isAgent ?? false,
            ),
          );
          setManagementRequestInfo({
            registrarionDate: formatDate(new Date()),
            refferenceNumber: IdCallCenter.toString(),
          });
          setIsUpdateLoading(false);
        })
        .catch((error) => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
          setIsUpdateLoading(false);
        });
    }
  };

  useEffect(() => {
    const suppliesDetailsAbortController = new AbortController();

    if (ownerData && userData && step === 0) {
      getSuppliesDetail(
        ownerData,
        userData.token,
        ownerDataCupSelected,
        suppliesDetailsAbortController.signal,
      );

      setIsCupsLoading(true);
    }
  }, [getSuppliesDetail, ownerData, ownerDataCupSelected, step, userData]);

  return (
    <Box component="section" className="management-update-gas-flow">
      <ManagementsUpdateHeader
        title={"Modificación de caudal diario (Qd)"}
        step={step}
        cupsDetail={cupsDetail}
        isLoading={isCupsLoading}
        showDetailCups={true}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={"2.5rem"}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        {step === -1 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="div"
                className="management-update-gas-flow__content-container"
                spacing={4}
              >
                <ManagementsUpdateGasFlowCupsSelection
                  ownerDataCups={ownerData?.cups || []}
                  ownerDataCupSelected={ownerDataCupSelected}
                  setOwnerDataCupSelected={setOwnerDataCupSelected}
                  setStep={setStep}
                />
              </Stack>
            </Grid>
          </Grid>
        )}
        {step === 0 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="div"
                className="management-update-gas-flow__content-container-alt"
                boxShadow={{
                  xs: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)",
                  md: "",
                }}
              >
                <ManagementsUpdateNewGasFlow
                  isAgent={isAgent}
                  cupsDetail={cupsDetail}
                  isLoading={isCupsLoading}
                  setFile={setFile}
                  handleSubmit={(incidenceText) =>
                    handleSubmit(incidenceText, ownerDataCupSelected)
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        )}
        {step === 1 && (
          <ManagementsRequestState
            showManagementRequestInfo
            isLoading={isUpdateLoading}
            state={requestState}
            managementRequestInfo={managementRequestInfo}
            title={requestStateData.title}
            description={requestStateData.description}
            descriptionExtra={requestStateData.descriptionExtra}
          />
        )}
      </Stack>
    </Box>
  );
};

export default ManagementsUpdateGasFlow;
